<script setup>
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  steps: {
    type: Array,
    required: true,
    default: () => [],
  },
  autoStart: {
    type: Boolean,
    default: false,
  },
  showStartButton: {
    type: Boolean,
    default: true,
  },
  driverOptions: {
    type: Object,
    default: () => ({
      showProgress: false,
      showButtons: true,
      overlayOpacity: 0.5,
      allowClose: true,
      disableActiveInteraction: true,
    }),
  },
  restartOnLastPrev: {
    type: Boolean,
    default: false,
  },
  onClickEndTour: {
    type: Function,
    default: () => {},
  },
  exitTour: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()

const driverInstance = driver({
  ...props.driverOptions,
  popoverClass: 'calendar-tour-popover',
  onPrevClick: () => {
    if (props.restartOnLastPrev && driverInstance.isLastStep()) {
      startTour(0)
    } else if (!driverInstance.isLastStep()) {
      driverInstance.movePrevious()
    }
    return true
  },
  onNextClick: () => {
    if (driverInstance.isLastStep() && props.onClickEndTour) {
      const index = driverInstance.getActiveIndex()
      props.onClickEndTour(index + 1)
    }
    driverInstance.moveNext()
  },
  onCloseClick: () => {
    const index = driverInstance.getActiveIndex()
    props.onClickEndTour(index + 1)
    driverInstance.destroy()
  },
})

onMounted(() => {
  driverInstance.setSteps(props.steps)
  if (props.autoStart) {
    startTour()
  }
})

const startTour = (stepIndex = 0) => {
  driverInstance.drive(stepIndex)
}

// Method to find step index by element ID or other identifier
const getStepIndex = identifier => {
  return props.steps.findIndex(
    step => step.element.includes(identifier) || step.id === identifier
  )
}

watch(
  () => props.exitTour,
  () => {
    if (props.exitTour) {
      driverInstance.destroy()
    }
  }
)

// Method to start tour from a specific element or step ID
const startTourFromStep = identifier => {
  const stepIndex = getStepIndex(identifier)
  if (stepIndex !== -1) {
    startTour(stepIndex)
  }
}

defineExpose({
  startTour,
  startTourFromStep,
})
</script>

<template>
  <div class="hidden">{{ t('tour.hidden_test_label') }}</div>
</template>

<style lang="scss">
.calendar-tour-popover {
  background-color: var(--primary-25) !important;
  color: var(--primary-600) !important;
  border-radius: 4px;
  padding: 12px;
  width: 280px;
  font-size: 12px;
  border: 1px solid var(--primary-300) !important;
  margin-top: 20px;
}

.calendar-tour-popover .driver-popover-title {
  color: var(--primary-700) !important;
  font-size: 13px;
  font-weight: 500;
  font-family: 'Inter' !important;

  .tour-step-number {
    color: var(--gray-600) !important;
    font-size: 13px;
    font-weight: 500;
    font-family: 'Inter' !important;
    margin-left: 4px;
  }
}

.calendar-tour-popover .driver-popover-description {
  color: var(--primary-600) !important;
  font-size: 12px;
  font-weight: 400 !important;
  font-family: 'Inter' !important;
  line-height: 1.6;
}

.calendar-tour-popover .driver-popover-navigation-btns {
  justify-content: flex-start !important;
  margin-top: 5px;
}

.calendar-tour-popover .driver-popover-footer button {
  font-weight: 500 !important;
  font-size: 12px !important;
  font-family: 'Inter' !important;
  gap: 10px !important;
}

.calendar-tour-popover .driver-popover-prev-btn {
  font-size: 12px;
  font-weight: 600;
  font-family: 'Inter' !important;
  color: var(--gray-700) !important;
  background: transparent !important;
  border: none !important;
}

.calendar-tour-popover .driver-popover-next-btn {
  font-size: 12px;
  font-weight: 600;
  font-family: 'Inter' !important;
  color: #fff !important;
  background: var(--primary-700) !important;
  border-radius: 4px !important;
  padding: 10px 12px !important;
  text-shadow: none !important;
  border: none !important;
}

.calendar-tour-popover .driver-popover-close-btn {
  margin-top: 3px !important;
}

#calendarsListContainer .driver-active-element {
  width: 34px !important;
  height: 34px !important;
  background-color: var(--primary-25) !important;
  border-radius: 50% !important;
  border: 1px solid var(--primary-300) !important;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

#calendarsListContainer .driver-active-element svg {
  stroke: var(--primary-700) !important;
  margin-left: 0px !important;
}
</style>
